import { getMercadoPagoInstance } from './mercadopago';

/**
 * Utilidades para montar y desmontar campos (fields) de MercadoPago de forma centralizada.
 * Provee funciones para montar los campos principales del checkout, campos dentro del modal y
 * para limpiarlos de forma segura.
 */

const DEFAULT_PLACEHOLDERS = {
  cardNumber: 'Número de la tarjeta',
  expirationDate: 'MM/YY',
  securityCode: 'Código de seguridad',
};

const mountedFields = new Map(); // containerId -> { field, fieldType }

const DEFAULT_CONTAINER_TIMEOUT_MS = 5000;
const CONTAINER_POLL_INTERVAL_MS = 50;

/**
 * Ejecuta el callback cuando el DOM esté listo (document.readyState !== 'loading').
 * Si ya está listo, ejecuta de inmediato; si no, espera a DOMContentLoaded.
 * @param {() => void} callback
 */
function whenDomReady(callback) {
  if (document.readyState !== 'loading') {
    callback();
  } else {
    document.addEventListener('DOMContentLoaded', callback, { once: true });
  }
}

/**
 * Espera a que exista en el DOM un elemento con el id dado.
 * @param {string} containerId - ID del elemento contenedor
 * @param {number} [timeoutMs=DEFAULT_CONTAINER_TIMEOUT_MS]
 * @returns {Promise<void>}
 */
function waitForContainer(containerId, timeoutMs = DEFAULT_CONTAINER_TIMEOUT_MS) {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      if (document.getElementById(containerId)) {
        resolve();
        return;
      }
      if (Date.now() - start >= timeoutMs) {
        reject(new Error(`Timeout: contenedor #${containerId} no encontrado en ${timeoutMs}ms`));
        return;
      }
      setTimeout(check, CONTAINER_POLL_INTERVAL_MS);
    };
    check();
  });
}

/**
 * Monta un field de Mercado Pago en un contenedor del DOM.
 * @param {string} fieldType - Tipo de campo: 'cardNumber' | 'expirationDate' | 'securityCode'
 * @param {string} containerId - ID del elemento contenedor
 * @param {object} [options={}] - Opciones para el SDK (p. ej. placeholder)
 * @returns {Promise<{ field: object, fieldType: string }>}
 */
async function mountField(fieldType, containerId, options = {}) {
  if (!containerId) throw new Error('containerId es requerido para montar field');
  if (mountedFields.has(containerId)) {
    return mountedFields.get(containerId);
  }

  const mp = await getMercadoPagoInstance();
  const createdField = mp.fields.create(fieldType, options);
  createdField.mount(containerId);
  mountedFields.set(containerId, { field: createdField, fieldType });
  return mountedFields.get(containerId);
}

/**
 * Desmonta un field y limpia el contenedor.
 * @param {string} containerId - ID del contenedor
 */
async function unmountField(containerId) {
  if (!containerId) return;
  const stored = mountedFields.get(containerId);
  if (stored && stored.field && stored.field.unmount) {
    try {
      stored.field.unmount();
    } catch (err) {
      // ignore
    }
    mountedFields.delete(containerId);
  }
  const el = document.getElementById(containerId);
  if (el) {
    while (el.firstChild) el.removeChild(el.firstChild);
  }
}

/**
 * Monta el campo de CVV en el modal (solo securityCode).
 * @param {object} [placeholders={}] - Placeholder para securityCode. Por defecto 'Código de seguridad'.
 * @returns {Promise<void>}
 */
async function ensureModalFields(placeholders = {}) {
  const p = { ...DEFAULT_PLACEHOLDERS, ...placeholders };
  await mountField('securityCode', 'cvv-modal__securityCode', { placeholder: p.securityCode });
}

async function cleanupMainFields() {
  await unmountField('form-checkout__cardNumber');
  await unmountField('form-checkout__expirationDate');
  await unmountField('form-checkout__securityCode');
}

async function cleanupModalFields() {
  await unmountField('cvv-modal__securityCode');
}

/**
 * Limpia los campos del formulario de edición de tarjeta.
 * @returns {Promise<void>}
 */
async function cleanupEditCardFields() {
  await unmountField('edit-card-form__cardNumber');
  await unmountField('edit-card-form__expirationDate');
  await unmountField('edit-card-form__securityCode');
}

async function cleanupAll() {
  const keys = Array.from(mountedFields.keys());
  await Promise.all(keys.map((k) => unmountField(k)));
}

export {
  mountField,
  unmountField,
  whenDomReady,
  waitForContainer,
  ensureModalFields,
  cleanupMainFields,
  cleanupModalFields,
  cleanupEditCardFields,
  cleanupAll,
};
